// Allocation tool request builders. Both flavours produce a "virtual
// grouping" Orion request -- one that asks just for a single grouping
// level rather than the full nested set the grid renders. Reuses the
// existing lib/buildOrionRequest builders so calcKey deduplication,
// derived-column synthesis, scope-filter compilation, and entity
// scoping all behave identically to the grid's queries.
//
// Two shapes:
//
//   - level (right-click breadcrumb): synthesize a config whose
//     `groupings` array contains just the level we want to chart.
//     Sum of the response rows equals the household total by construction
//     -- Orion does the rollup server-side, so no client tree-walking
//     and no lazy-expand gap.
//
//   - row (right-click grid row): reuse the grid's lazy-expand child
//     request path (buildChildRequest for entity-scopable parents,
//     buildFilteredChildRequest for virtual ancestors), capped at a
//     single child grouping level via maxGroupingDepth. Sum of the
//     response rows equals the parent row's value.
//
// Both flavours strip benchmarks (donut doesn't need the comparison
// fan-out) and preserve config.scopeFilters intact.

import { buildOrionRequest, buildChildRequest, buildFilteredChildRequest } from '../../../lib/buildOrionRequest'
import { ENTITY_SCOPABLE_GROUPINGS } from '../../../lib/constants'

/**
 * Strip benchmarks. Allocation is a single-measure chart and the
 * Comparison grouping fan-out is wasted bytes (and would clutter the
 * response transform). Keep scopeFilters intact so the chart honours
 * whatever scope the user has active in the grid.
 */
function withoutBenchmarks (config) {
  return { ...config, includeBenchmarks: false }
}

/**
 * All builders return `{ request, unwrap }` so the hook can apply the
 * same outer-wrapper unwrap the grid's lazy-expand does for virtual
 * (non-entity-scopable) groupings:
 *
 *   - request: the Orion request body to POST
 *   - unwrap:  null if the response rows are already what we want, or
 *              `{ name, groupEntityId }` if the response is wrapped in
 *              an outer "filter" grouping and we need to dive into the
 *              matching row's `.data` to get the actual children. Same
 *              identity comparison as `fetchRowChildren` in
 *              usePortfolioExplorerQuery.js.
 */

/**
 * Build a request that returns one row per group at `depth` rolled up
 * at the household level. Used by the breadcrumb's `View allocation`.
 *
 * @param {object} args
 * @param {object} args.config -- the live explorer config (with full groupings)
 * @param {string} args.asOfDate
 * @param {string} args.entity -- e.g. 'Household'
 * @param {Array<number>} args.entityIds
 * @param {number} args.depth -- index into config.groupings
 * @returns `{ request, unwrap: null }`, or null if depth is out of range.
 */
export function buildLevelRequest ({ config, asOfDate, entity, entityIds, depth }) {
  if (depth < 0 || depth >= (config.groupings?.length || 0)) return null
  const grouping = config.groupings[depth]
  const synthesized = {
    ...withoutBenchmarks(config),
    groupings: [grouping]
  }
  return {
    request: buildOrionRequest(synthesized, { asOfDate, entity, entityIds }),
    unwrap: null
  }
}

/**
 * Build a request that returns one row per direct child of `row` at the
 * next grouping level. Used by the row context menu's
 * `View allocation of children`.
 *
 * The row's grouping wire value (`row.grouping`) determines dispatch:
 * if it's entity-scopable (Account / Registration / Household) we
 * scope directly via entity/entityIds; otherwise we fall back to the
 * filtered-child path that wraps in a string-equal filter the same way
 * the grid's lazy expand does for virtual groupings. In the filtered
 * case the response is wrapped in an outer "filter" grouping, so the
 * returned `unwrap` carries enough identity to find and unwrap it
 * downstream (see useAllocationQuery).
 *
 * @param {object} args
 * @param {object} args.config -- the live explorer config (with full groupings)
 * @param {string} args.asOfDate
 * @param {string} args.entity -- household-level entity
 * @param {Array<number>} args.entityIds -- household-level ids
 * @param {object} args.row -- the right-clicked row (must carry grouping,
 *   groupEntityId, name, depth, and _ancestorEntity/_ancestorEntityIds
 *   for virtual-grouping rows)
 * @returns `{ request, unwrap }`, or null if row is at the leaf level.
 */
export function buildRowRequest ({ config, asOfDate, entity, entityIds, row }) {
  if (!row) return null
  const groupings = config.groupings || []
  const groupingIndex = groupings.indexOf(row.grouping)
  if (groupingIndex < 0) return null
  const childDepthIndex = groupingIndex + 1
  if (childDepthIndex >= groupings.length) return null

  const sanitized = withoutBenchmarks(config)
  const isScopable = ENTITY_SCOPABLE_GROUPINGS.has(row.grouping)

  if (isScopable) {
    return {
      request: buildChildRequest(sanitized, {
        asOfDate,
        depthIndex: childDepthIndex,
        maxGroupingDepth: 1,
        entity: row.grouping,
        entityIds: [row.groupEntityId]
      }),
      unwrap: null
    }
  }

  // Virtual grouping ancestor -- scope via the nearest entity-scopable
  // ancestor (captured by transformGroupNode) plus a string-equal
  // filter on the row's grouping/name. Same dispatch the grid uses.
  // The response is wrapped in an outer grouping; unwrap by row
  // identity below.
  const ancestorEntity = row._ancestorEntity || entity
  const ancestorEntityIds = row._ancestorEntityIds || entityIds
  return {
    request: buildFilteredChildRequest(sanitized, {
      asOfDate,
      depthIndex: childDepthIndex,
      maxGroupingDepth: 1,
      entity: ancestorEntity,
      entityIds: ancestorEntityIds,
      filterGrouping: row.grouping,
      filterName: row.name
    }),
    unwrap: { name: row.name, groupEntityId: row.groupEntityId ?? null }
  }
}
